import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Filter, DollarSign, TrendingUp } from "lucide-react";
import api from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const STAGES = [
  { key: "cta_viewed", label: "CTA Views", hint: "Homepage 'Need Something Sourced?' section seen", color: "bg-slate-400" },
  { key: "submit_rfq_clicked", label: "Submit RFQ Clicks", hint: "Visitors who clicked an RFQ button", color: "bg-sky-400" },
  { key: "rfq_started", label: "RFQs Started", hint: "Visitors who began filling the RFQ form", color: "bg-[var(--pg-accent)]" },
  { key: "rfq_submitted", label: "RFQs Submitted", hint: "Website RFQ submissions (tracked)", color: "bg-blue-600" },
  { key: "quotes_sent", label: "Quotes Sent", hint: "Customer quotes sent or accepted", color: "bg-indigo-600" },
  { key: "orders_won", label: "Orders Won", hint: "Accepted quotes converted to orders", color: "bg-green-600" },
];

export default function Funnel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/funnel").then((r) => setData(r.data)).catch(() => toast.error("Failed to load funnel"));
  }, []);

  if (!data) return <div className="text-slate-400 text-[14px]" data-testid="funnel-loading">Loading funnel…</div>;

  const max = Math.max(1, ...STAGES.map((s) => data[s.key] || 0));

  return (
    <div data-testid="admin-funnel-page">
      <div className="flex items-center gap-3">
        <Filter size={22} className="text-[var(--pg-accent)]" />
        <div>
          <h1 className="font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Conversion Funnel</h1>
          <p className="text-[13px] text-slate-500">Website visitors → RFQs → quotes → orders → revenue</p>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="funnel-revenue-card">
          <div className="flex items-center gap-2 text-[12px] font-[Poppins] font-bold uppercase tracking-wider text-slate-500"><DollarSign size={14} /> Revenue Collected</div>
          <div className="mt-2 font-[Poppins] font-extrabold text-[28px] text-[var(--pg-navy)]">{money(data.revenue_paid)}</div>
          <div className="text-[12px] text-slate-400">Paid invoices, all time</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="funnel-profit-card">
          <div className="flex items-center gap-2 text-[12px] font-[Poppins] font-bold uppercase tracking-wider text-slate-500"><TrendingUp size={14} /> Gross Profit (Won)</div>
          <div className="mt-2 font-[Poppins] font-extrabold text-[28px] text-green-700">{money(data.gross_profit_won)}</div>
          <div className="text-[12px] text-slate-400">Across accepted quotes</div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funnel-engagement-row">
        {[
          ["phone_click", "Phone Clicks", "tel: taps (mobile bar, heroes, footer)"],
          ["email_click", "Email Clicks", "mailto: clicks"],
          ["inquiry_submitted", "Short Inquiries", "Contact-form submissions"],
          ["capability_statement_download", "Capability Statement", "PDF downloads"],
          ["inquiries_total", "Inquiries in DB", "All stored contact messages"],
        ].map(([k, label, hint]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`funnel-metric-${k}`}>
            <div className="text-[11px] font-[Poppins] font-bold uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 font-[Poppins] font-extrabold text-[24px] text-[var(--pg-navy)]">{data[k] || 0}</div>
            <div className="text-[11.5px] text-slate-400">{hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="funnel-chart">
        <h3 className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">Funnel Stages</h3>
        <p className="mt-1 text-[12.5px] text-slate-400">Website tracking began with the conversion CTA launch — early stages count from that date. Total RFQs in the system (all sources): <strong className="text-[var(--pg-navy)]">{data.rfqs_total}</strong></p>
        <div className="mt-6 space-y-4">
          {STAGES.map((s, i) => {
            const val = data[s.key] || 0;
            const prev = i > 0 ? (data[STAGES[i - 1].key] || 0) : null;
            const conv = prev ? Math.round((val / Math.max(1, prev)) * 100) : null;
            return (
              <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
                <div className="flex items-baseline justify-between">
                  <div className="text-[13px] font-[Poppins] font-semibold text-[var(--pg-navy)]">{s.label}
                    <span className="ml-2 text-[11.5px] font-normal text-slate-400">{s.hint}</span>
                  </div>
                  <div className="text-[13px] font-[Poppins] font-bold text-[var(--pg-navy)]">{val}
                    {conv !== null && <span className={`ml-2 text-[11.5px] font-semibold ${conv >= 50 ? "text-green-600" : "text-slate-400"}`}>{conv}% of prev</span>}
                  </div>
                </div>
                <div className="mt-1.5 h-6 bg-slate-100 rounded-md overflow-hidden">
                  <div className={`h-full rounded-md ${s.color} transition-[width]`} style={{ width: `${Math.max(2, (val / max) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
